import type { Flashcard, Lesson, Question, Skill, Topic } from '@/data/types';
import { card, choice, example, f, numeric, p, text, tip, warn } from '../../authoring';

/**
 * Biznes i zarządzanie, dział 3: finanse osobiste i rynek finansowy.
 *
 * Podstawa programowa 2024: ZP III.8, IV.1–IV.4, IV.8–IV.17;
 * ZR III.1–III.12, III.14–III.15. Stawki (np. podatek od zysków
 * kapitałowych 19%) podane w treści zadań.
 */

export const FIN_TOPIC: Topic = {
  id: 'biz-finance-topic',
  subjectId: 'biz',
  name: 'Finanse osobiste i rynek finansowy',
  summary: 'Pieniądz i instytucje finansowe, budżet domowy i zadłużenie, usługi banków, procent składany, inwestowanie, ubezpieczenia i ochrona konsumenta.',
};

export const FIN_SKILLS: Skill[] = [
  {
    id: 'biz-money-institutions',
    topicId: 'biz-finance-topic',
    name: 'Pieniądz i instytucje rynku finansowego',
    level: 'PR',
    ckeRequirement: 'Funkcje i formy pieniądza, obieg pieniądza, instytucje rynku finansowego, funkcje NBP (ZP IV.1, IV.3; ZR III.11, III.12)',
    prerequisites: ['biz-fiscal-monetary'],
    examValue: 0.7,
  },
  {
    id: 'biz-household',
    topicId: 'biz-finance-topic',
    name: 'Budżet domowy i spirala zadłużenia',
    level: 'PR',
    ckeRequirement: 'Postawy wobec pieniędzy, zasady budżetu gospodarstwa domowego, unikanie spirali zadłużenia (ZP IV.2, IV.4)',
    prerequisites: ['biz-money-institutions'],
    examValue: 0.6,
  },
  {
    id: 'biz-banking',
    topicId: 'biz-finance-topic',
    name: 'Usługi banków: konta, lokaty, kredyty',
    level: 'PR',
    ckeRequirement: 'Oferty banków, SKOK i instytucji pożyczkowych, realna stopa procentowa, gwarancje depozytów, bezpieczeństwo bankowości elektronicznej, umowy produktów finansowych (ZP IV.10, IV.11, IV.15)',
    prerequisites: ['biz-household'],
    examValue: 0.85,
  },
  {
    id: 'biz-compound-saving',
    topicId: 'biz-finance-topic',
    name: 'Procent składany i oszczędzanie',
    level: 'PR',
    ckeRequirement: 'Oszczędzanie i inwestowanie na emeryturę, procent składany, formy oszczędzania: rentowność, ryzyko, płynność (ZP IV.8, IV.12)',
    prerequisites: ['biz-banking'],
    examValue: 0.85,
  },
  {
    id: 'biz-investing',
    topicId: 'biz-finance-topic',
    name: 'Inwestowanie: akcje, obligacje, fundusze',
    level: 'PR',
    ckeRequirement: 'Papiery wartościowe, GPW, fundusze inwestycyjne, ryzyko i jego ograniczanie, dywersyfikacja, pułapki psychologiczne inwestorów (ZP IV.9, IV.14; ZR III.1–III.9)',
    prerequisites: ['biz-compound-saving'],
    examValue: 0.9,
  },
  {
    id: 'biz-insurance',
    topicId: 'biz-finance-topic',
    name: 'Ubezpieczenia',
    level: 'PR',
    ckeRequirement: 'System zabezpieczenia społecznego, ubezpieczenia osobowe i majątkowe, OWU, wyłączenia odpowiedzialności (ZP IV.13; ZR III.10)',
    prerequisites: ['biz-banking'],
    examValue: 0.7,
  },
  {
    id: 'biz-consumer-protection',
    topicId: 'biz-finance-topic',
    name: 'Prawa konsumenta i etyka w finansach',
    level: 'PR',
    ckeRequirement: 'Prawa konsumenta i reklamacja, instytucje prokonsumenckie, ochrona konsumenta na rynku finansowym, etyka i zrównoważone finanse (ZP III.8, IV.16, IV.17; ZR III.14, III.15)',
    prerequisites: ['biz-banking'],
    examValue: 0.7,
  },
];

// ===========================================================================
// Lekcje
// ===========================================================================

export const FIN_LESSONS: Lesson[] = [
  {
    skillId: 'biz-money-institutions',
    minutes: 12,
    intro:
      'Pieniądz jest umową społeczną: przyjmujemy go, bo wierzymy, że inni też go przyjmą. Pełni kilka funkcji naraz, a nad jego wartością i bezpieczeństwem czuwa kilka instytucji.',
    blocks: [
      p('Funkcje pieniądza: środek wymiany (płacimy nim), miernik wartości (wyrażamy ceny), środek przechowywania wartości (oszczędzamy) i środek płatniczy (spłacamy długi). Formy: gotówkowy (banknoty, monety) i bezgotówkowy — zapisy na rachunkach, karty, przelewy, BLIK.'),
      p('Instytucje rynku finansowego w Polsce: NBP — bank centralny (emituje pieniądz, prowadzi politykę pieniężną, jest bankiem banków i bankiem państwa, zarządza rezerwami walutowymi). KNF — nadzoruje banki, ubezpieczycieli i rynek kapitałowy. BFG — gwarantuje depozyty. GPW — giełda papierów wartościowych. Rzecznik Finansowy — pomaga klientom w sporach z instytucjami finansowymi.'),
      tip('Bankiem centralnym jest NBP — nie PKO BP. Bank komercyjny przyjmuje depozyty klientów i udziela im kredytów.'),
      warn('Emisja pieniądza (banknotów i monet) to wyłączne zadanie NBP — nie Ministerstwa Finansów.'),
    ],
    examples: [
      example(
        'Kupujesz rower za 1200 zł i porównujesz go z innym za 1500 zł. Które funkcje pieniądza się tu ujawniają?',
        [['Porównanie cen — miernik wartości.', 'ceny wyrażone w złotych'], 'Zapłata — środek wymiany.'],
        'miernik wartości i środek wymiany',
      ),
      example(
        'Która instytucja nadzoruje banki w Polsce?',
        ['Nadzór nad rynkiem finansowym to zadanie KNF.', 'NBP prowadzi politykę pieniężną.'],
        'Komisja Nadzoru Finansowego',
      ),
    ],
    pitfalls: ['Mylenie NBP z bankami komercyjnymi.', 'Przypisywanie nadzoru bankowego NBP zamiast KNF.', 'Pomijanie funkcji przechowywania wartości.'],
  },
  {
    skillId: 'biz-household',
    minutes: 12,
    intro:
      'Budżet domowy to plan: ile wpływa, ile wydajemy i na co. Bez planu łatwo o sytuację, w której pożyczka spłaca inną pożyczkę — to spirala zadłużenia.',
    blocks: [
      p('Przychody gospodarstwa: wynagrodzenia, świadczenia, dochody z działalności. Wydatki stałe (czynsz, raty, media) i zmienne (jedzenie, rozrywka). Budżet jest zrównoważony, gdy wydatki nie przekraczają dochodów; nadwyżka to oszczędności.'),
      p('Dobre praktyki: najpierw odkładaj (np. stały procent dochodu), buduj poduszkę finansową na 3–6 miesięcy wydatków, notuj wydatki, porównuj oferty. Rata wszystkich kredytów nie powinna pochłaniać dużej części dochodu.'),
      p('Spirala zadłużenia zaczyna się, gdy raty przekraczają możliwości, a luka jest łatana kolejnymi pożyczkami (często drogimi, pozabankowymi). Wyjście: pełna lista długów, rozmowa z wierzycielami, restrukturyzacja, konsolidacja na lepszych warunkach, cięcie wydatków — a nie kolejna „chwilówka”.'),
      tip('Udział wydatku w budżecie = wydatek : dochód · 100%.'),
      warn('Kredyt konsolidacyjny obniża ratę, ale zwykle wydłuża okres spłaty i podnosi łączny koszt.'),
    ],
    examples: [
      example(
        'Dochody rodziny to 6000 zł, a wydatki stałe 3900 zł. Jaki to procent dochodu?',
        [['3900 : 6000 = 0,65.', 'wydatek przez dochód'], 'Razy 100%.'],
        '65%',
      ),
      example(
        'Co jest pierwszym krokiem wyjścia ze spirali zadłużenia?',
        ['Spisanie wszystkich długów, rat i terminów.', 'Dopiero wtedy da się zaplanować spłatę i rozmawiać z wierzycielami.'],
        'pełna lista zobowiązań',
      ),
    ],
    pitfalls: ['Łatanie spłat kolejnymi pożyczkami.', 'Brak poduszki finansowej.', 'Traktowanie niższej raty jako tańszego kredytu.'],
  },
  {
    skillId: 'biz-banking',
    minutes: 15,
    intro:
      'Bank przechowuje pieniądze, prowadzi rozliczenia i pożycza. Porównując oferty, trzeba patrzeć nie na reklamę, tylko na liczby: realne oprocentowanie lokaty i całkowity koszt kredytu (RRSO).',
    blocks: [
      p('Konto osobiste służy do codziennych rozliczeń (opłaty za prowadzenie, kartę, wypłaty z bankomatów). Lokata terminowa daje odsetki za zamrożenie pieniędzy na określony czas. Depozyty w bankach i SKOK-ach są gwarantowane przez BFG do równowartości 100 000 euro na osobę w jednym banku.'),
      p('RRSO (rzeczywista roczna stopa oprocentowania) obejmuje nie tylko odsetki, ale i prowizje, ubezpieczenia i inne opłaty — to najlepsza miara kosztu kredytu. Całkowity koszt kredytu = suma wszystkich wpłat − pożyczona kwota. Pożyczki pozabankowe bywają wielokrotnie droższe.'),
      f('\\text{odsetki netto} = \\text{kwota} \\cdot r \\cdot \\frac{\\text{miesiące}}{12} \\cdot (1 - 0{,}19)', 'Podatek od odsetek (tzw. podatek Belki): 19%.'),
      p('Bezpieczeństwo bankowości elektronicznej: bank nigdy nie prosi o hasło ani kod BLIK w SMS-ie czy telefonie; sprawdzaj adres strony, nie instaluj aplikacji „do zdalnej pomocy” na prośbę rozmówcy, używaj uwierzytelniania dwuskładnikowego.'),
      tip('Porównując kredyty, porównuj RRSO i całkowitą kwotę do zapłaty, a nie wysokość raty.'),
      warn('Oprocentowanie lokaty jest podawane w skali roku — lokata 3-miesięczna na 4% daje odsetki za ćwierć roku.'),
    ],
    examples: [
      example(
        'Lokata 12 000 zł na 3 miesiące, oprocentowanie 4% w skali roku, podatek 19%. Ile wyniosą odsetki netto?',
        [['Brutto: 12 000 · 0,04 · 3/12 = 120 zł.', 'ćwierć roku'], 'Netto: 120 · 0,81 = 97,20 zł.'],
        '97,20 zł',
      ),
      example(
        'Pożyczka 5000 zł spłacana w 12 ratach po 470 zł. Ile wynosi jej całkowity koszt?',
        ['Suma wpłat: 12 · 470 = 5640 zł.', 'Minus pożyczona kwota: 640 zł.'],
        '640 zł',
      ),
    ],
    pitfalls: ['Liczenie odsetek za cały rok przy lokacie krótszej niż rok.', 'Pominięcie podatku od odsetek.', 'Porównywanie kredytów wyłącznie po racie.'],
  },
  {
    skillId: 'biz-compound-saving',
    minutes: 14,
    intro:
      'Procent składany to „odsetki od odsetek”: po każdym okresie odsetki dopisuje się do kapitału, więc w kolejnym okresie pracują one razem z nim. W długim czasie daje to ogromną różnicę — dlatego na emeryturę warto oszczędzać wcześnie.',
    blocks: [
      f('K_n = K_0 \\cdot (1 + r)^n', 'Kapitał po n latach przy rocznej kapitalizacji.'),
      p('Realna stopa procentowa uwzględnia inflację: $1 + r_{\\text{real}} = \\frac{1 + r_{\\text{nom}}}{1 + i}$, w przybliżeniu $r_{\\text{nom}} - i$. Lokata na 6% przy inflacji 4% powiększa siłę nabywczą tylko o ok. 1,9% — a po podatku 19% jeszcze mniej.'),
      p('Formy oszczędzania ocenia się według trzech cech: rentowności (ile zarobisz), ryzyka (czy możesz stracić) i płynności (jak szybko odzyskasz pieniądze bez straty). Lokata: niskie ryzyko, niska rentowność, średnia płynność. Akcje: wyższy potencjalny zysk i wyższe ryzyko.'),
      tip('Reguła 72: liczba lat do podwojenia kapitału ≈ 72 : oprocentowanie w procentach (przy 6% — ok. 12 lat).'),
      warn('Inflacja wyższa od oprocentowania oznacza, że oszczędności realnie tracą wartość, choć liczba złotych rośnie.'),
    ],
    examples: [
      example(
        'Ile będzie po 3 latach z 10 000 zł na 5% rocznie (kapitalizacja roczna, bez podatku)?',
        [['$10\\,000 \\cdot 1{,}05^3$.', 'odsetki dopisywane co rok'], '$= 11\\,576{,}25$ zł.'],
        '11 576,25 zł',
      ),
      example(
        'Lokata daje 6%, inflacja wynosi 4%. Ile wynosi realna stopa procentowa?',
        ['$1{,}06 : 1{,}04 \\approx 1{,}0192$.', 'Realnie ok. 1,92% (w przybliżeniu 2%).'],
        'ok. 1,92%',
      ),
    ],
    pitfalls: ['Liczenie procentu prostego zamiast składanego.', 'Ignorowanie inflacji.', 'Ocenianie inwestycji tylko po rentowności, bez ryzyka i płynności.'],
  },
  {
    skillId: 'biz-investing',
    minutes: 16,
    intro:
      'Inwestowanie to lokowanie pieniędzy z nadzieją na zysk — zawsze z ryzykiem straty. Każdy instrument ma inny poziom ryzyka i inną płynność, a mądry inwestor dobiera je do celu, horyzontu i własnej odporności na straty.',
    blocks: [
      p('Akcja to udział we własności spółki — daje prawo do dywidendy i głosu na walnym zgromadzeniu; jej cena na giełdzie (GPW) zmienia się. Obligacja to dług: emitent (państwo, firma) zobowiązuje się oddać pieniądze z odsetkami. Obligacje skarbowe są zwykle bezpieczniejsze od korporacyjnych. Fundusz inwestycyjny zbiera pieniądze wielu osób i inwestuje je przez zawodowego zarządzającego (za opłatą).'),
      f('\\text{stopa zwrotu} = \\frac{\\text{cena sprzedaży} + \\text{dywidendy} - \\text{cena zakupu}}{\\text{cena zakupu}} \\cdot 100\\%'),
      p('Ryzyko ogranicza się dywersyfikacją (rozłożeniem pieniędzy na różne instrumenty, branże, kraje), dopasowaniem do horyzontu i unikaniem inwestowania pożyczonych pieniędzy. Rynek pieniężny to instrumenty krótkoterminowe (do roku, np. bony skarbowe), kapitałowy — długoterminowe (akcje, obligacje).'),
      p('Pułapki psychologiczne: owczy pęd (kupowanie, bo wszyscy kupują), nadmierna pewność siebie, niechęć do realizacji strat, gonienie za „pewnymi” obietnicami wysokiego zysku. Inwestowanie to nie hazard, ale obietnica wysokiego zysku bez ryzyka to sygnał oszustwa (np. piramida finansowa).'),
      tip('Analiza fundamentalna ocenia kondycję spółki (zyski, zadłużenie, branża), analiza techniczna — wykresy notowań.'),
      warn('Akcjonariusz nie ma gwarancji dywidendy ani zwrotu zainwestowanej kwoty — gwarancja BFG dotyczy depozytów bankowych, nie akcji ani funduszy.'),
    ],
    examples: [
      example(
        'Kupiono akcję za 50 zł, otrzymano 2 zł dywidendy i sprzedano akcję za 58 zł. Jaka była stopa zwrotu?',
        [['Zysk: 58 + 2 − 50 = 10 zł.', 'dywidenda też jest zyskiem'], '10 : 50 = 20%.'],
        '20%',
      ),
      example(
        'Dlaczego inwestor rozkłada pieniądze na kilka różnych spółek i obligacje?',
        ['Strata na jednej inwestycji nie niszczy całego portfela.', 'To dywersyfikacja ryzyka.'],
        'żeby ograniczyć ryzyko',
      ),
    ],
    pitfalls: ['Mylenie akcji (udział) z obligacją (dług).', 'Przekonanie, że fundusz inwestycyjny gwarantuje zysk.', 'Pominięcie dywidendy w stopie zwrotu.'],
  },
  {
    skillId: 'biz-insurance',
    minutes: 13,
    intro:
      'Ubezpieczenie przenosi ryzyko dużej straty na ubezpieczyciela w zamian za składkę. System ubezpieczeń społecznych (ZUS) i zdrowotnych (NFZ) jest obowiązkowy dla pracujących, a ubezpieczenia gospodarcze — osobowe i majątkowe — w większości dobrowolne.',
    blocks: [
      p('Ubezpieczenia społeczne: emerytalne, rentowe, chorobowe i wypadkowe (ZUS). Zdrowotne — NFZ finansuje leczenie. Ubezpieczenia osobowe dotyczą życia i zdrowia (na życie, NNW, turystyczne), majątkowe — rzeczy i odpowiedzialności (mieszkanie, AC samochodu, OC).'),
      p('OC posiadacza pojazdu jest obowiązkowe i pokrywa szkody, które kierowca wyrządzi innym. AC jest dobrowolne i pokrywa szkody we własnym aucie. Szczegóły umowy opisują OWU (ogólne warunki ubezpieczenia), w tym wyłączenia odpowiedzialności (np. szkody z winy umyślnej, pod wpływem alkoholu) i sumę ubezpieczenia.'),
      f('\\text{odszkodowanie} = \\text{szkoda} \\cdot \\frac{\\text{suma ubezpieczenia}}{\\text{wartość mienia}}', 'Zasada proporcji przy niedoubezpieczeniu.'),
      tip('Franszyza redukcyjna (udział własny) to kwota, którą ubezpieczyciel odejmuje od każdego odszkodowania.'),
      warn('Wyższa suma ubezpieczenia to wyższa składka — ale zaniżenie sumy (niedoubezpieczenie) obniża odszkodowanie proporcjonalnie.'),
    ],
    examples: [
      example(
        'Dom wart 400 000 zł ubezpieczono na 300 000 zł. Szkoda wyniosła 40 000 zł. Ile wypłaci ubezpieczyciel (zasada proporcji)?',
        [['Suma ubezpieczenia to 3/4 wartości.', '300 000 : 400 000'], '40 000 · 3/4 = 30 000 zł.'],
        '30 000 zł',
      ),
      example(
        'Które ubezpieczenie komunikacyjne jest obowiązkowe?',
        ['OC chroni osoby poszkodowane przez kierowcę.', 'AC i NNW są dobrowolne.'],
        'OC posiadacza pojazdu',
      ),
    ],
    pitfalls: ['Mylenie OC z AC.', 'Nieczytanie wyłączeń w OWU.', 'Zaniżanie sumy ubezpieczenia dla niższej składki.'],
  },
  {
    skillId: 'biz-consumer-protection',
    minutes: 13,
    intro:
      'Konsument ma silniejszą ochronę prawną niż przedsiębiorca, bo zwykle wie mniej niż sprzedawca. Warto znać swoje prawa i instytucje, które pomagają je egzekwować — także na rynku finansowym.',
    blocks: [
      p('Towar niezgodny z umową można reklamować — sprzedawca odpowiada za wady ujawnione w ciągu 2 lat od wydania towaru i ma 14 dni na odpowiedź na reklamację (brak odpowiedzi oznacza jej uznanie). Umowę zawartą przez internet lub poza lokalem firmy konsument może odstąpić w ciągu 14 dni bez podawania przyczyny. Od umowy kredytu konsumenckiego — też w ciągu 14 dni.'),
      p('Pomoc: miejski lub powiatowy rzecznik konsumentów (sprawy indywidualne), UOKiK (zbiorowe interesy konsumentów, niedozwolone klauzule), Rzecznik Finansowy (spory z bankami i ubezpieczycielami po odrzuceniu reklamacji), KNF (nadzór), Inspekcja Handlowa.'),
      p('Etyka w finansach: nieuczciwe praktyki to m.in. missellling (sprzedaż produktu niedopasowanego do klienta), ukrywanie kosztów, wprowadzanie w błąd. Zrównoważone finanse biorą pod uwagę także skutki społeczne i środowiskowe inwestycji (czynniki ESG).'),
      tip('Reklamację składaj na piśmie lub mailem i zachowaj dowód — termin 14 dni na odpowiedź biegnie od jej złożenia.'),
      warn('Prawo odstąpienia od umowy bez podania przyczyny dotyczy zakupów na odległość — nie zakupów w sklepie stacjonarnym (tam sklep może, ale nie musi, przyjąć zwrot).'),
    ],
    examples: [
      example(
        'Kupiłeś słuchawki w sklepie internetowym. Po tygodniu stwierdzasz, że ci się nie podobają, choć działają. Co możesz zrobić?',
        [['Umowa zawarta na odległość.', 'prawo odstąpienia'], 'Masz 14 dni na odstąpienie bez podania przyczyny.'],
        'odstąpić od umowy',
      ),
      example(
        'Bank odrzucił reklamację dotyczącą błędnie pobranej opłaty. Do kogo zwrócić się po pomoc?',
        ['Spory klientów z instytucjami finansowymi — Rzecznik Finansowy.', 'Może prowadzić postępowanie polubowne.'],
        'Rzecznik Finansowy',
      ),
    ],
    pitfalls: ['Mylenie reklamacji (wada) ze zwrotem (zmiana zdania).', 'Przekonanie, że w sklepie stacjonarnym zawsze można oddać towar.', 'Kierowanie indywidualnej skargi do KNF zamiast do Rzecznika Finansowego.'],
  },
];

// ===========================================================================
// Zadania
// ===========================================================================

export const FIN_QUESTIONS: Question[] = [
  // biz-money-institutions ----------------------------------------------------
  choice({
    id: 'bf-m-1',
    skill: 'biz-money-institutions',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Porównujesz ceny dwóch telefonów wyrażone w złotych. Z której funkcji pieniądza korzystasz?',
    choices: ['miernika wartości', 'środka przechowywania wartości', 'środka płatniczego przy spłacie długu', 'środka tezauryzacji'],
    answer: 'A',
    hints: ['Co robisz — płacisz, oszczędzasz czy porównujesz?', 'Porównujesz wartość dwóch towarów.', 'W jakiej jednostce wyrażone są ceny?', 'Pieniądz służy tu do mierzenia wartości.'],
    steps: ['Ceny w złotych pozwalają porównać wartość towarów.', 'To funkcja miernika wartości.'],
    errors: [
      ['B', 'Przechowywanie wartości to oszczędzanie.', 'Porównywanie cen to miernik wartości.'],
      ['C', 'Nie spłacasz długu.', 'Porównywanie cen to miernik wartości.'],
      ['D', 'Tezauryzacja to gromadzenie pieniądza.', 'Porównywanie cen to miernik wartości.'],
    ],
  }),
  choice({
    id: 'bf-m-2',
    skill: 'biz-money-institutions',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Która instytucja nadzoruje w Polsce banki, zakłady ubezpieczeń i rynek kapitałowy?',
    choices: ['Komisja Nadzoru Finansowego', 'Narodowy Bank Polski', 'Bankowy Fundusz Gwarancyjny', 'Giełda Papierów Wartościowych'],
    answer: 'A',
    hints: ['Która z nazw zawiera słowo „nadzór”?', 'Nadzór to kontrola, czy instytucje działają zgodnie z prawem.', 'NBP prowadzi politykę pieniężną, BFG gwarantuje depozyty.', 'GPW to miejsce handlu akcjami.'],
    steps: ['Nadzór nad rynkiem finansowym sprawuje KNF.', 'NBP — polityka pieniężna, BFG — gwarancje depozytów, GPW — obrót papierami.'],
    errors: [
      ['B', 'NBP prowadzi politykę pieniężną.', 'Nadzór sprawuje KNF.'],
      ['C', 'BFG gwarantuje depozyty.', 'Nadzór sprawuje KNF.'],
      ['D', 'GPW organizuje obrót papierami wartościowymi.', 'Nadzór sprawuje KNF.'],
    ],
  }),
  text({
    id: 'bf-m-3',
    skill: 'biz-money-institutions',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Uzupełnij skrót: wyłączne prawo emisji znaków pieniężnych (banknotów i monet) w Polsce ma ________ (podaj skrót nazwy instytucji).',
    answer: 'NBP',
    variants: ['Narodowy Bank Polski'],
    hints: ['Która instytucja jest bankiem centralnym?', 'To „bank banków” i bank państwa.', 'Jej banknoty nosisz w portfelu.', 'Skrót ma trzy litery.'],
    steps: ['Emisja pieniądza to zadanie banku centralnego.', 'W Polsce bankiem centralnym jest NBP.'],
    errors: [['MF', 'Ministerstwo Finansów nie emituje pieniądza.', 'Emisja to zadanie NBP.']],
  }),
  choice({
    id: 'bf-m-4',
    skill: 'biz-money-institutions',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Które zadanie NIE należy do Narodowego Banku Polskiego?',
    choices: ['udzielanie kredytów hipotecznych osobom prywatnym', 'prowadzenie polityki pieniężnej', 'zarządzanie rezerwami walutowymi', 'prowadzenie rachunków budżetu państwa'],
    answer: 'A',
    hints: ['Z kim NBP prowadzi rozliczenia — z bankami i państwem czy z osobami prywatnymi?', 'NBP to bank banków i bank państwa.', 'Kto udziela kredytów hipotecznych klientom?', 'Banki komercyjne.'],
    steps: ['NBP nie obsługuje klientów indywidualnych.', 'Kredyty hipoteczne udzielają banki komercyjne.'],
    errors: [
      ['B', 'To podstawowe zadanie NBP.', 'Szukamy zadania, które do NBP nie należy.'],
      ['C', 'NBP zarządza rezerwami walutowymi.', 'Szukamy zadania, które do NBP nie należy.'],
      ['D', 'NBP jest bankiem państwa.', 'Szukamy zadania, które do NBP nie należy.'],
    ],
  }),
  choice({
    id: 'bf-m-5',
    skill: 'biz-money-institutions',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Który sposób zapłaty jest przykładem pieniądza bezgotówkowego?',
    choices: ['przelew z rachunku bankowego', 'zapłata banknotem 100 zł', 'zapłata monetami', 'zapłata banknotami wypłaconymi z bankomatu'],
    answer: 'A',
    hints: ['Czy przy tej płatności do rąk sprzedawcy trafiają banknoty lub monety?', 'Pieniądz bezgotówkowy to zapis na rachunku.', 'Która płatność zmienia tylko salda na kontach?', 'Przelew, karta, BLIK.'],
    steps: ['Przelew przesuwa pieniądz między rachunkami — to pieniądz bezgotówkowy.', 'Banknoty i monety to gotówka.'],
    errors: [
      ['B', 'Banknot to gotówka.', 'Bezgotówkowy jest przelew.'],
      ['C', 'Monety to gotówka.', 'Bezgotówkowy jest przelew.'],
      ['D', 'Bankomat wypłaca gotówkę; późniejsza płatność tymi banknotami jest gotówkowa.', 'Bezgotówkowy jest przelew między rachunkami.'],
    ],
  }),
  choice({
    id: 'bf-m-6',
    skill: 'biz-money-institutions',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Klient ma w jednym banku 50 000 euro na lokacie, a w innym 80 000 euro. Oba banki zbankrutowały. Jak działa gwarancja BFG (limit: równowartość 100 000 euro)?',
    choices: ['odzyska obie kwoty w całości, bo limit dotyczy każdego banku osobno', 'odzyska łącznie tylko 100 000 euro', 'odzyska tylko 50 000 euro z pierwszego banku', 'nic nie odzyska, bo gwarancja dotyczy tylko złotych'],
    answer: 'A',
    hints: ['Czy limit gwarancji liczy się na osobę w jednym banku, czy we wszystkich razem?', 'Na osobę w jednym banku.', 'Czy któraś z kwot przekracza limit w swoim banku?', 'Porównaj każdą z nich ze 100 000 euro.'],
    steps: ['Gwarancja BFG obejmuje do 100 000 euro na osobę w każdym banku osobno.', 'Obie kwoty mieszczą się w limicie — klient odzyska całość.'],
    errors: [
      ['B', 'Limit nie sumuje się między bankami.', 'Liczy się każdy bank osobno.'],
      ['C', 'Druga kwota też mieści się w limicie.', 'Liczy się każdy bank osobno.'],
      ['D', 'Gwarancja obejmuje też depozyty w walutach.', 'Limit wyrażony jest w euro.'],
    ],
  }),
  choice({
    id: 'bf-m-7',
    skill: 'biz-money-institutions',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'W którym przypadku pieniądz NIE pełni dobrze funkcji środka przechowywania wartości?',
    choices: ['gdy inflacja jest bardzo wysoka i ceny rosną z miesiąca na miesiąc', 'gdy inflacja jest niska i stabilna', 'gdy pieniądze leżą na lokacie oprocentowanej powyżej inflacji', 'gdy obowiązuje stały kurs waluty i stabilne ceny'],
    answer: 'A',
    hints: ['Co musi się dziać z siłą nabywczą, żeby oszczędzanie w pieniądzu miało sens?', 'Musi być w miarę stabilna.', 'Co wysoka inflacja robi z siłą nabywczą?', 'Szybko ją obniża — ludzie wolą wydać pieniądze albo kupić rzeczy.'],
    steps: ['Przy wysokiej inflacji pieniądz szybko traci siłę nabywczą.', 'Przestaje dobrze przechowywać wartość.'],
    errors: [
      ['B', 'Przy niskiej inflacji pieniądz dobrze przechowuje wartość.', 'Problem powoduje wysoka inflacja.'],
      ['C', 'Lokata powyżej inflacji chroni wartość.', 'Problem powoduje wysoka inflacja.'],
      ['D', 'Stabilne ceny sprzyjają przechowywaniu wartości.', 'Problem powoduje wysoka inflacja.'],
    ],
  }),

  // biz-household -------------------------------------------------------------
  numeric({
    id: 'bf-h-1',
    skill: 'biz-household',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Miesięczne dochody rodziny wynoszą 6000 zł, a wydatki stałe (czynsz, raty, media) 3900 zł. Jaki procent dochodu pochłaniają wydatki stałe?',
    answer: 65,
    verify: () => (3900 / 6000) * 100,
    hints: ['Jak obliczyć, jaką częścią całości jest wydatek?', 'Podziel wydatek przez dochód.', 'Wynik pomnóż przez 100%.', 'Sprawdź: połowa dochodu to 3000 zł, więc wynik jest większy od 50%.'],
    steps: ['3900 : 6000 = 0,65.', '0,65 · 100% = 65%.'],
    errors: [['35', 'Podany procent pozostałej części dochodu.', 'Pytanie dotyczy wydatków stałych.']],
  }),
  choice({
    id: 'bf-h-2',
    skill: 'biz-household',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Co to jest poduszka finansowa?',
    choices: ['własne oszczędności na nieprzewidziane wydatki', 'kredyt odnawialny do wykorzystania w razie potrzeby', 'limit debetowy na rachunku osobistym', 'pieniądze odłożone na zaplanowane wakacje'],
    answer: 'A',
    hints: ['Na co przydają się pieniądze, gdy nagle stracisz pracę albo zepsuje się pralka?', 'Na pokrycie nieplanowanych wydatków.', 'Czy to pieniądze własne, czy pożyczone?', 'Własne — odłożone wcześniej.'],
    steps: ['Poduszka finansowa to własne oszczędności na nieprzewidziane sytuacje.', 'Zaleca się 3–6 miesięcy wydatków.'],
    errors: [
      ['B', 'Kredyt to pieniądze pożyczone.', 'Poduszka to własne oszczędności.'],
      ['C', 'Debet to dług.', 'Poduszka to własne oszczędności.'],
      ['D', 'To oszczędności na konkretny, zaplanowany cel.', 'Poduszka jest na wydatki nieprzewidziane.'],
    ],
  }),
  choice({
    id: 'bf-h-3',
    skill: 'biz-household',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Które zachowanie najbardziej zwiększa ryzyko wpadnięcia w spiralę zadłużenia?',
    choices: ['spłacanie rat jednego kredytu kolejnymi szybkimi pożyczkami', 'odkładanie 10% dochodu co miesiąc', 'porównywanie RRSO przed zaciągnięciem kredytu', 'prowadzenie zapisków wydatków'],
    answer: 'A',
    hints: ['Czym jest spirala zadłużenia?', 'Sytuacją, w której dług rośnie, bo nowe pożyczki spłacają stare.', 'Które zachowanie zwiększa łączne zadłużenie?', 'Pozostałe to dobre praktyki.'],
    steps: ['Spłacanie długu nowym długiem powiększa zadłużenie i koszty.', 'To mechanizm spirali zadłużenia.'],
    errors: [
      ['B', 'To dobra praktyka oszczędzania.', 'Spiralę napędza spłacanie długu długiem.'],
      ['C', 'Porównywanie RRSO chroni przed drogim kredytem.', 'Spiralę napędza spłacanie długu długiem.'],
      ['D', 'Zapisywanie wydatków pomaga panować nad budżetem.', 'Spiralę napędza spłacanie długu długiem.'],
    ],
  }),
  numeric({
    id: 'bf-h-4',
    skill: 'biz-household',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Ania zarabia 4500 zł netto miesięcznie. Stałe wydatki wynoszą 2700 zł, a zmienne 1350 zł. Resztę odkłada. Po ilu pełnych miesiącach zgromadzi poduszkę finansową równą trzymiesięcznym wydatkom (stałym i zmiennym)?',
    answer: 27,
    verify: () => Math.ceil((3 * (2700 + 1350)) / (4500 - 2700 - 1350)),
    hints: ['Ile Ania odkłada co miesiąc?', 'Dochód minus wszystkie wydatki.', 'Ile ma wynosić poduszka?', 'Trzy razy miesięczne wydatki — potem podziel i zaokrąglij w górę.'],
    steps: ['Oszczędza 4500 − 2700 − 1350 = 450 zł miesięcznie; poduszka: 3 · 4050 = 12 150 zł.', '12 150 : 450 = 27 miesięcy.'],
    errors: [['18', 'Poduszka liczona tylko z wydatków stałych.', 'Trzymiesięczne wydatki to stałe i zmienne.']],
  }),
  choice({
    id: 'bf-h-5',
    skill: 'biz-household',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Pan Kowalski ma trzy drogie pożyczki. Bank proponuje kredyt konsolidacyjny: jedna niższa rata, ale okres spłaty wydłużony z 3 do 8 lat. Które stwierdzenie jest prawdziwe?',
    choices: ['rata spadnie, ale łączny koszt kredytu może wzrosnąć', 'konsolidacja zawsze zmniejsza łączny koszt zadłużenia', 'bank spłaci stare pożyczki, więc dług zniknie', 'dłuższy okres spłaty zawsze obniża całkowity koszt'],
    answer: 'A',
    hints: ['Od czego zależy łączna kwota odsetek?', 'Od oprocentowania i czasu, przez który pożyczasz.', 'Co się dzieje z odsetkami, gdy spłacasz dłużej?', 'Płacisz je przez więcej lat.'],
    steps: ['Niższa rata ułatwia bieżące spłaty.', 'Dłuższy okres oznacza zwykle więcej zapłaconych odsetek łącznie.'],
    errors: [
      ['B', 'Dłuższa spłata często podnosi łączny koszt.', 'Trzeba porównać całkowite kwoty do zapłaty.'],
      ['C', 'Konsolidacja zamienia długi na jeden, nie usuwa ich.', 'Dług pozostaje do spłaty.'],
      ['D', 'Dłuższy okres zwykle zwiększa sumę odsetek.', 'Porównuj całkowity koszt.'],
    ],
  }),
  choice({
    id: 'bf-h-6',
    skill: 'biz-household',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Które działanie jest najlepszym PIERWSZYM krokiem dla osoby, która nie nadąża ze spłatą kilku zobowiązań?',
    choices: ['spisanie długów i terminów oraz rozmowa z wierzycielami o warunkach spłaty', 'zaciągnięcie szybkiej pożyczki na spłatę najbliższej raty', 'wstrzymanie wszystkich spłat do czasu poprawy sytuacji', 'spłacenie w pierwszej kolejności najmniejszego długu kartą kredytową'],
    answer: 'A',
    hints: ['Co trzeba wiedzieć, żeby zaplanować wyjście z długów?', 'Ile, komu i kiedy trzeba spłacić.', 'Czy wierzyciele mogą zmienić warunki spłaty?', 'Często tak — np. rozłożyć zaległość na raty.'],
    steps: ['Pełna lista zobowiązań pozwala zaplanować spłatę.', 'Rozmowa z wierzycielami może dać restrukturyzację; nowe pożyczki pogłębiają problem.'],
    errors: [
      ['B', 'To klasyczny mechanizm spirali zadłużenia.', 'Nowy dług nie rozwiązuje problemu.'],
      ['C', 'Brak spłat szybko uruchamia windykację i odsetki karne.', 'Lepiej negocjować warunki.'],
      ['D', 'Karta kredytowa to kolejny dług.', 'Najpierw pełna lista długów i rozmowa z wierzycielami.'],
    ],
  }),

  // biz-banking ---------------------------------------------------------------
  choice({
    id: 'bf-b-1',
    skill: 'biz-banking',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Który wskaźnik najlepiej pokazuje całkowity koszt kredytu konsumenckiego, łącznie z prowizjami i opłatami?',
    choices: ['RRSO', 'oprocentowanie nominalne', 'wysokość pierwszej raty', 'stopa referencyjna NBP'],
    answer: 'A',
    hints: ['Czy oprocentowanie nominalne uwzględnia prowizję?', 'Nie — tylko odsetki.', 'Który wskaźnik zawiera wszystkie koszty?', 'Rzeczywista roczna stopa oprocentowania.'],
    steps: ['RRSO obejmuje odsetki, prowizje, ubezpieczenia i inne opłaty.', 'Pozwala porównać oferty różnych banków.'],
    errors: [
      ['B', 'Oprocentowanie nominalne pomija prowizje.', 'Całkowity koszt pokazuje RRSO.'],
      ['C', 'Rata nie pokazuje łącznego kosztu.', 'Całkowity koszt pokazuje RRSO.'],
      ['D', 'Stopa NBP to parametr polityki pieniężnej.', 'Całkowity koszt pokazuje RRSO.'],
    ],
  }),
  numeric({
    id: 'bf-b-2',
    skill: 'biz-banking',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Pożyczkę 5000 zł spłacono w 12 ratach po 470 zł. Oblicz całkowity koszt pożyczki (w zł).',
    answer: 640,
    verify: () => 12 * 470 - 5000,
    hints: ['Ile łącznie oddał pożyczkobiorca?', '12 rat po 470 zł.', 'Ile z tej kwoty to zwrot pożyczonych pieniędzy?', 'Koszt to nadwyżka ponad pożyczoną kwotę.'],
    steps: ['Suma wpłat: 12 · 470 = 5640 zł.', 'Koszt: 5640 − 5000 = 640 zł.'],
    errors: [['5640', 'Podana suma wpłat zamiast kosztu.', 'Odejmij pożyczoną kwotę.']],
  }),
  numeric({
    id: 'bf-b-3',
    skill: 'biz-banking',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Założono lokatę 12 000 zł na 3 miesiące. Oprocentowanie wynosi 4% w skali roku, a od odsetek pobierany jest podatek 19%. Oblicz odsetki netto (w zł).',
    answer: '97,2',
    variants: ['97.2', '97,20', '97.20'],
    tolerance: 0.001,
    verify: () => 12000 * 0.04 * (3 / 12) * 0.81,
    hints: ['Za jaką część roku naliczane są odsetki?', 'Za 3 z 12 miesięcy.', 'Odsetki brutto: kwota · oprocentowanie · 3/12.', 'Od odsetek odejmij 19% podatku.'],
    steps: ['Brutto: 12 000 · 0,04 · 3/12 = 120 zł.', 'Netto: 120 · 0,81 = 97,20 zł.'],
    errors: [['480', 'Odsetki policzone za cały rok i bez podatku.', 'Oprocentowanie jest roczne — lokata trwa kwartał.']],
  }),
  choice({
    id: 'bf-b-4',
    skill: 'biz-banking',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Dostajesz SMS-a „z banku” z linkiem do „dopłaty 1,50 zł, żeby odblokować konto”. Co zrobić?',
    choices: ['nie klikać linku i sprawdzić konto samodzielnie w aplikacji banku', 'kliknąć link i szybko dopłacić, żeby nie stracić dostępu', 'odpisać na SMS-a z pytaniem, czy wiadomość na pewno jest z banku', 'kliknąć link, ale nie podawać na stronie numeru karty'],
    answer: 'A',
    hints: ['Czy bank prosi o dopłaty przez linki w SMS-ach?', 'Nie — to typowa metoda wyłudzeń (phishing).', 'Jak bezpiecznie sprawdzić stan konta?', 'Samodzielnie — przez aplikację albo oficjalną infolinię.'],
    steps: ['Link w SMS-ie z prośbą o dopłatę to typowy phishing.', 'Sprawdź konto samodzielnie i zgłoś próbę oszustwa bankowi.'],
    errors: [
      ['B', 'Tak działa phishing — strona przejmie dane.', 'Nie klikaj linków z takich wiadomości.'],
      ['C', 'Odpowiadasz oszustowi — on potwierdzi wszystko.', 'Sprawdzaj kanałem, który sam wybierzesz.'],
      ['D', 'Fałszywa strona wyłudzi inne dane, np. login lub kod.', 'Nie klikaj linków z takich wiadomości.'],
    ],
  }),
  choice({
    id: 'bf-b-5',
    skill: 'biz-banking',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Dwie oferty pożyczki na 10 000 zł: A — oprocentowanie 8%, prowizja 0 zł, RRSO 8,3%; B — oprocentowanie 5%, prowizja 1500 zł, RRSO 13,9%. Którą wybrać, jeśli liczy się łączny koszt?',
    choices: ['A — ma niższe RRSO, więc łącznie kosztuje mniej', 'B — ma niższe oprocentowanie', 'obie kosztują tyle samo', 'nie da się ocenić bez rozmowy z doradcą'],
    answer: 'A',
    hints: ['Który wskaźnik obejmuje prowizję?', 'RRSO.', 'Której oferty RRSO jest niższe?', 'Niskie oprocentowanie B nadrabia wysoka prowizja.'],
    steps: ['RRSO uwzględnia prowizję — oferta A ma niższe RRSO.', 'Łączny koszt oferty A jest niższy mimo wyższego oprocentowania.'],
    errors: [
      ['B', 'Niskie oprocentowanie ukrywa wysoką prowizję.', 'Porównuj RRSO.'],
      ['C', 'RRSO obu ofert się różni.', 'Porównuj RRSO.'],
      ['D', 'RRSO pozwala porównać oferty samodzielnie.', 'Niższe RRSO to niższy koszt.'],
    ],
  }),
  text({
    id: 'bf-b-6',
    skill: 'biz-banking',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Uzupełnij skrót: depozyty w bankach i SKOK-ach są w Polsce gwarantowane do równowartości 100 000 euro przez ________ (podaj skrót nazwy instytucji).',
    answer: 'BFG',
    variants: ['Bankowy Fundusz Gwarancyjny'],
    hints: ['Która instytucja wypłaca pieniądze klientom upadłego banku?', 'Fundusz gwarancyjny.', 'Jak brzmi jego pełna nazwa?', 'Bankowy Fundusz Gwarancyjny.'],
    steps: ['Depozyty gwarantuje Bankowy Fundusz Gwarancyjny.', 'Limit: równowartość 100 000 euro na osobę w jednym banku.'],
    errors: [['KNF', 'KNF sprawuje nadzór, nie gwarantuje depozytów.', 'Gwarantem jest BFG.']],
  }),
  choice({
    id: 'bf-b-7',
    skill: 'biz-banking',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Firma pożyczkowa reklamuje „pierwszą pożyczkę za 0 zł”, ale w umowie jest opłata 300 zł za każde przedłużenie terminu spłaty o 30 dni. Jak ocenić tę ofertę?',
    choices: ['może być bardzo droga, jeśli spłata się opóźni', 'jest darmowa, bo reklama mówi o pożyczce za 0 zł', 'opłata za przedłużenie jest nielegalna, więc nie trzeba jej płacić', 'jest tańsza od każdego kredytu bankowego'],
    answer: 'A',
    hints: ['Czy reklama opisuje wszystkie warunki umowy?', 'Nie — ważne koszty bywają w umowie i tabeli opłat.', 'Co się stanie, gdy spłata się przeciągnie?', 'Każde przedłużenie dokłada 300 zł kosztu.'],
    steps: ['Oferta „0 zł” dotyczy tylko terminowej spłaty.', 'Przy opóźnieniu opłaty za przedłużenia szybko rosną — trzeba je policzyć przed podpisaniem.'],
    errors: [
      ['B', 'Reklama nie zastępuje lektury umowy.', 'Liczą się warunki z umowy.'],
      ['C', 'Opłaty z umowy obowiązują, jeśli nie są klauzulą niedozwoloną.', 'Trzeba je uwzględnić w kosztach.'],
      ['D', 'Pożyczki pozabankowe bywają dużo droższe.', 'Porównuj całkowity koszt.'],
    ],
  }),

  // biz-compound-saving -------------------------------------------------------
  choice({
    id: 'bf-c-1',
    skill: 'biz-compound-saving',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Na czym polega procent składany?',
    choices: ['odsetki dopisywane do kapitału same zaczynają przynosić odsetki', 'odsetki są wypłacane zawsze od początkowej kwoty', 'bank pobiera prowizję od każdej wpłaty', 'oprocentowanie rośnie co roku o stałą liczbę punktów'],
    answer: 'A',
    hints: ['Co się dzieje z odsetkami po kapitalizacji?', 'Są dopisywane do kapitału.', 'Od jakiej kwoty liczone są odsetki w następnym okresie?', 'Od większej — powiększonej o poprzednie odsetki.'],
    steps: ['Po kapitalizacji odsetki powiększają kapitał.', 'W kolejnym okresie odsetki liczy się od większej kwoty — „odsetki od odsetek”.'],
    errors: [
      ['B', 'To procent prosty.', 'W procencie składanym odsetki pracują dalej.'],
      ['C', 'Prowizje nie mają związku z procentem składanym.', 'Chodzi o odsetki od odsetek.'],
      ['D', 'Oprocentowanie może być stałe.', 'Chodzi o dopisywanie odsetek do kapitału.'],
    ],
  }),
  numeric({
    id: 'bf-c-2',
    skill: 'biz-compound-saving',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Ile będzie wynosił kapitał 10 000 zł po 3 latach przy oprocentowaniu 5% rocznie i rocznej kapitalizacji odsetek (pomiń podatek)? Podaj wynik w zł z dokładnością do grosza.',
    answer: '11576,25',
    variants: ['11576.25', '11 576,25'],
    tolerance: 0.001,
    verify: () => Math.round(10000 * 1.05 ** 3 * 100) / 100,
    hints: ['Jaki wzór opisuje kapitał po n latach?', '$K_n = K_0 \\cdot (1 + r)^n$.', 'Podstaw $K_0 = 10\\,000$, $r = 0{,}05$, $n = 3$.', 'Policz $1{,}05^3$ i pomnóż.'],
    steps: ['$1{,}05^3 = 1{,}157625$.', '$10\\,000 \\cdot 1{,}157625 = 11\\,576{,}25$ zł.'],
    errors: [['11500', 'Policzony procent prosty.', 'Odsetki są dopisywane do kapitału co roku.']],
  }),
  numeric({
    id: 'bf-c-3',
    skill: 'biz-compound-saving',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Oprocentowanie lokaty wynosi 6% rocznie, a inflacja 4%. Oblicz realną stopę procentową dokładnie (wzorem Fishera), w %, z dokładnością do dwóch miejsc po przecinku (pomiń podatek).',
    answer: '1,92',
    variants: ['1.92'],
    tolerance: 0.005,
    verify: () => Math.round((1.06 / 1.04 - 1) * 10000) / 100,
    hints: ['Jak inflacja wpływa na zysk z lokaty?', 'Zmniejsza siłę nabywczą odsetek.', '$1 + r_{real} = \\frac{1{,}06}{1{,}04}$.', 'Odejmij 1 i zamień na procenty.'],
    steps: ['$1{,}06 : 1{,}04 \\approx 1{,}01923$.', 'Realna stopa ≈ 1,92%.'],
    errors: [['2', 'Użyte przybliżenie 6% − 4%.', 'Dokładny wzór to iloraz wskaźników.']],
  }),
  numeric({
    id: 'bf-c-4',
    skill: 'biz-compound-saving',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Po ilu pełnych latach kapitał co najmniej się podwoi przy oprocentowaniu 6% rocznie i rocznej kapitalizacji (pomiń podatek)?',
    answer: 12,
    verify: () => {
      let lata = 0;
      let k = 1;
      while (k < 2) {
        k *= 1.06;
        lata += 1;
      }
      return lata;
    },
    hints: ['Jaki warunek ma spełnić kapitał?', '$1{,}06^n \\ge 2$.', 'Reguła 72 daje szybkie oszacowanie: 72 : 6.', 'Sprawdź, czy po tylu latach kapitał naprawdę przekroczył dwukrotność.'],
    steps: ['$1{,}06^{11} \\approx 1{,}90$ — jeszcze za mało.', '$1{,}06^{12} \\approx 2{,}01$ — kapitał się podwoił po 12 latach.'],
    errors: [['17', 'Policzony procent prosty (100 : 6).', 'Przy procencie składanym podwojenie następuje szybciej.']],
  }),
  choice({
    id: 'bf-c-5',
    skill: 'biz-compound-saving',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Które uporządkowanie form oszczędzania od NAJWIĘKSZEJ do najmniejszej płynności jest poprawne?',
    choices: ['rachunek oszczędnościowy, lokata 12-miesięczna, mieszkanie na wynajem', 'mieszkanie na wynajem, lokata 12-miesięczna, rachunek oszczędnościowy', 'lokata 12-miesięczna, mieszkanie na wynajem, rachunek oszczędnościowy', 'mieszkanie na wynajem, rachunek oszczędnościowy, lokata 12-miesięczna'],
    answer: 'A',
    hints: ['Co oznacza płynność inwestycji?', 'Jak szybko i bez straty można zamienić ją na gotówkę.', 'Pieniądze z którego produktu wypłacisz od ręki?', 'Sprzedaż mieszkania trwa najdłużej.'],
    steps: ['Rachunek oszczędnościowy — wypłata od ręki; lokatę można zerwać z utratą odsetek.', 'Mieszkanie sprzedaje się tygodniami lub miesiącami — najmniejsza płynność.'],
    errors: [
      ['B', 'Odwrócona kolejność.', 'Najbardziej płynny jest rachunek oszczędnościowy.'],
      ['C', 'Rachunek jest bardziej płynny niż lokata.', 'Najbardziej płynny jest rachunek oszczędnościowy.'],
      ['D', 'Mieszkanie ma najmniejszą płynność.', 'Najbardziej płynny jest rachunek oszczędnościowy.'],
    ],
  }),
  numeric({
    id: 'bf-c-6',
    skill: 'biz-compound-saving',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Lokata roczna daje 6%, od odsetek pobierany jest podatek 19%, a inflacja wynosi 4%. Oblicz realny zysk po podatku (w %, dokładnie wzorem Fishera, z dokładnością do dwóch miejsc po przecinku).',
    answer: '0,83',
    variants: ['0.83'],
    tolerance: 0.005,
    verify: () => Math.round(((1 + 0.06 * 0.81) / 1.04 - 1) * 10000) / 100,
    hints: ['Ile wynosi oprocentowanie po podatku?', '6% · 0,81 = 4,86%.', 'Teraz uwzględnij inflację wzorem Fishera.', '$1{,}0486 : 1{,}04$, odejmij 1.'],
    steps: ['Oprocentowanie netto: 4,86%.', '$1{,}0486 : 1{,}04 \\approx 1{,}0083$ — realnie ok. 0,83%.'],
    errors: [['1,92', 'Pominięty podatek od odsetek.', 'Najpierw odejmij 19% podatku od odsetek.']],
  }),

  // biz-investing -------------------------------------------------------------
  choice({
    id: 'bf-i-1',
    skill: 'biz-investing',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Czym różni się akcja od obligacji?',
    choices: ['akcja to udział we własności, a obligacja to dług emitenta', 'akcja to dług spółki, a obligacja to udział w jej własności', 'akcja daje stały zysk, a obligacja zysk zależny od wyników', 'nie ma różnicy — to dwie nazwy tego samego papieru'],
    answer: 'A',
    hints: ['Kim staje się kupujący akcję?', 'Współwłaścicielem spółki.', 'A kupujący obligację?', 'Wierzycielem — pożycza emitentowi pieniądze.'],
    steps: ['Akcja daje udział we własności (dywidenda, głos na walnym).', 'Obligacja to dług z obietnicą zwrotu i odsetek.'],
    errors: [
      ['B', 'Odwrócone definicje.', 'Akcja to własność, obligacja to dług.'],
      ['C', 'Odwrotnie — to obligacja ma zwykle ustalone odsetki.', 'Akcja to własność, obligacja to dług.'],
      ['D', 'To różne instrumenty.', 'Akcja to własność, obligacja to dług.'],
    ],
  }),
  numeric({
    id: 'bf-i-2',
    skill: 'biz-investing',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Inwestor kupił akcję za 50 zł, otrzymał 2 zł dywidendy i sprzedał akcję za 58 zł. Oblicz stopę zwrotu z inwestycji (w %, pomiń prowizje i podatki).',
    answer: 20,
    verify: () => ((58 + 2 - 50) / 50) * 100,
    hints: ['Ile inwestor zarobił łącznie?', 'Różnica cen plus dywidenda.', 'Do jakiej kwoty porównujesz zysk?', 'Do ceny zakupu — potem razy 100%.'],
    steps: ['Zysk: (58 − 50) + 2 = 10 zł.', '10 : 50 · 100% = 20%.'],
    errors: [['16', 'Pominięta dywidenda.', 'Dywidenda też jest częścią zysku.']],
  }),
  choice({
    id: 'bf-i-3',
    skill: 'biz-investing',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Co to jest dywersyfikacja portfela inwestycyjnego?',
    choices: ['rozłożenie pieniędzy na różne instrumenty, branże i rynki, żeby ograniczyć ryzyko', 'zainwestowanie wszystkich pieniędzy w jedną spółkę o najwyższym zysku w zeszłym roku', 'częste kupowanie i sprzedawanie akcji tej samej spółki', 'inwestowanie wyłącznie pożyczonych pieniędzy'],
    answer: 'A',
    hints: ['Co się stanie, jeśli cały majątek jest w jednej spółce, która zbankrutuje?', 'Stracisz wszystko.', 'Jak ograniczyć skutki jednej złej inwestycji?', 'Nie wkładać wszystkich jajek do jednego koszyka.'],
    steps: ['Dywersyfikacja rozkłada środki na wiele różnych inwestycji.', 'Strata jednej części nie niszczy całego portfela.'],
    errors: [
      ['B', 'To koncentracja ryzyka.', 'Dywersyfikacja rozkłada ryzyko.'],
      ['C', 'To spekulacja, nie dywersyfikacja.', 'Dywersyfikacja to różne instrumenty.'],
      ['D', 'Inwestowanie na kredyt zwiększa ryzyko.', 'Dywersyfikacja je ogranicza.'],
    ],
  }),
  choice({
    id: 'bf-i-4',
    skill: 'biz-investing',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Który instrument należy do rynku pieniężnego (krótkoterminowego)?',
    choices: ['bony skarbowe o terminie wykupu kilku miesięcy', 'akcje dużych spółek notowanych na GPW', 'obligacje skarbowe o terminie wykupu 10 lat', 'jednostki funduszu inwestującego w akcje'],
    answer: 'A',
    hints: ['Czym różni się rynek pieniężny od kapitałowego?', 'Horyzontem: do roku albo dłużej.', 'Który instrument ma termin krótszy niż rok?', 'Akcje nie mają terminu wykupu.'],
    steps: ['Rynek pieniężny to instrumenty do roku — np. bony skarbowe.', 'Akcje, długie obligacje i fundusze akcji należą do rynku kapitałowego.'],
    errors: [
      ['B', 'Akcje to rynek kapitałowy.', 'Rynek pieniężny: instrumenty do roku.'],
      ['C', 'Obligacje 10-letnie to rynek kapitałowy.', 'Rynek pieniężny: instrumenty do roku.'],
      ['D', 'Fundusz akcji inwestuje długoterminowo.', 'Rynek pieniężny: instrumenty do roku.'],
    ],
  }),
  choice({
    id: 'bf-i-5',
    skill: 'biz-investing',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Znajomy namawia do wpłaty: „gwarantowane 5% zysku tygodniowo, bez ryzyka, zysk z wpłat nowych uczestników”. Jak to ocenić?',
    choices: ['to cechy piramidy finansowej — sygnał oszustwa', 'to bezpieczna inwestycja, bo zysk jest gwarantowany', 'to fundusz inwestycyjny, bo zyski dzieli się między uczestników', 'to lokata, tylko z wyższym oprocentowaniem'],
    answer: 'A',
    hints: ['Czy istnieje wysoki zysk bez ryzyka?', 'Nie — wyższy zysk zawsze wiąże się z wyższym ryzykiem.', 'Skąd pochodzą wypłaty w tym schemacie?', 'Z wpłat nowych uczestników — to piramida.'],
    steps: ['Wypłaty z wpłat nowych osób to mechanizm piramidy finansowej.', 'Obietnica wysokiego, pewnego zysku to typowy sygnał oszustwa.'],
    errors: [
      ['B', 'Takiej gwarancji nie da się uczciwie dać.', 'To sygnał oszustwa.'],
      ['C', 'Fundusz zarabia na inwestycjach, a nie na wpłatach nowych osób.', 'Opis pasuje do piramidy.'],
      ['D', 'Lokaty nie dają 5% tygodniowo.', 'Opis pasuje do piramidy.'],
    ],
  }),
  choice({
    id: 'bf-i-6',
    skill: 'biz-investing',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Inwestor trzyma akcje, które straciły 40%, bo „sprzeda, jak wrócą do ceny zakupu”, choć sytuacja spółki się pogorszyła. Jaki błąd psychologiczny popełnia?',
    choices: ['niechęć do realizacji straty (efekt dyspozycji)', 'nadmierną dywersyfikację portfela', 'efekt stadny — kupowanie tego, co kupują inni', 'nadmierną pewność siebie przy wyborze nowych spółek'],
    answer: 'A',
    hints: ['Czy cena zakupu ma wpływ na to, ile spółka jest warta dziś?', 'Nie — rynek jej nie pamięta.', 'Dlaczego inwestor nie sprzedaje?', 'Bo sprzedaż „przypieczętowałaby” stratę.'],
    steps: ['Trzymanie tracących akcji tylko po to, by uniknąć realizacji straty, to efekt dyspozycji.', 'Decyzja powinna zależeć od perspektyw spółki, nie od ceny zakupu.'],
    errors: [
      ['B', 'Dywersyfikacja to rozkładanie ryzyka — tu chodzi o jedną spółkę.', 'Tu działa niechęć do straty.'],
      ['C', 'Inwestor nie naśladuje innych — trzyma się ceny zakupu.', 'Tu działa niechęć do straty.'],
      ['D', 'Opis nie dotyczy wyboru nowych spółek.', 'Tu działa niechęć do straty.'],
    ],
  }),
  choice({
    id: 'bf-i-7',
    skill: 'biz-investing',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Maturzystka ma 5000 zł, które będą jej potrzebne za rok na studia. Która alokacja najlepiej pasuje do jej celu i horyzontu?',
    choices: ['lokata lub obligacje skarbowe o krótkim terminie', 'akcje jednej małej spółki technologicznej', 'kryptowaluty kupione na kredyt', 'fundusz akcji rynków wschodzących'],
    answer: 'A',
    hints: ['Jak długi jest horyzont inwestycji?', 'Rok — to krótko.', 'Czy może sobie pozwolić na stratę?', 'Nie — pieniądze są potrzebne na konkretny cel.'],
    steps: ['Krótki horyzont i konkretny cel wymagają niskiego ryzyka.', 'Lokata lub krótkoterminowe obligacje skarbowe chronią kapitał.'],
    errors: [
      ['B', 'Jedna mała spółka to duże ryzyko.', 'Krótki horyzont wymaga bezpiecznych instrumentów.'],
      ['C', 'Kredyt i wysoka zmienność to skrajne ryzyko.', 'Krótki horyzont wymaga bezpiecznych instrumentów.'],
      ['D', 'Fundusz akcji jest na długi horyzont.', 'Krótki horyzont wymaga bezpiecznych instrumentów.'],
    ],
  }),

  // biz-insurance -------------------------------------------------------------
  choice({
    id: 'bf-u-1',
    skill: 'biz-insurance',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Które ubezpieczenie komunikacyjne jest w Polsce obowiązkowe dla posiadacza samochodu?',
    choices: ['OC posiadacza pojazdu', 'AC (autocasco)', 'NNW kierowcy i pasażerów', 'assistance'],
    answer: 'A',
    hints: ['Kogo chroni ubezpieczenie OC?', 'Osoby, którym kierowca wyrządzi szkodę.', 'Dlaczego państwo wymaga właśnie tej polisy?', 'Żeby poszkodowani zawsze dostali odszkodowanie.'],
    steps: ['OC posiadacza pojazdu jest obowiązkowe.', 'AC, NNW i assistance są dobrowolne.'],
    errors: [
      ['B', 'AC jest dobrowolne.', 'Obowiązkowe jest OC.'],
      ['C', 'NNW jest dobrowolne.', 'Obowiązkowe jest OC.'],
      ['D', 'Assistance jest dobrowolne.', 'Obowiązkowe jest OC.'],
    ],
  }),
  choice({
    id: 'bf-u-2',
    skill: 'biz-insurance',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Które ubezpieczenie należy do ubezpieczeń społecznych w ZUS?',
    choices: ['ubezpieczenie emerytalne', 'ubezpieczenie mieszkania od pożaru', 'ubezpieczenie turystyczne', 'autocasco'],
    answer: 'A',
    hints: ['Jakie ryzyka obejmuje system ubezpieczeń społecznych?', 'Starość, niezdolność do pracy, chorobę, wypadek.', 'Która instytucja wypłaca emerytury?', 'ZUS.'],
    steps: ['Ubezpieczenia społeczne: emerytalne, rentowe, chorobowe i wypadkowe.', 'Pozostałe to dobrowolne ubezpieczenia gospodarcze.'],
    errors: [
      ['B', 'To ubezpieczenie majątkowe.', 'Społeczne jest emerytalne.'],
      ['C', 'To dobrowolne ubezpieczenie osobowe.', 'Społeczne jest emerytalne.'],
      ['D', 'To ubezpieczenie majątkowe.', 'Społeczne jest emerytalne.'],
    ],
  }),
  numeric({
    id: 'bf-u-3',
    skill: 'biz-insurance',
    kind: 'typical',
    difficulty: 3,
    prompt: 'W polisie AC jest franszyza redukcyjna (udział własny) 500 zł. Szkoda w samochodzie wyniosła 8000 zł. Ile wypłaci ubezpieczyciel (w zł)?',
    answer: 7500,
    verify: () => 8000 - 500,
    hints: ['Czym jest franszyza redukcyjna?', 'Kwotą, którą ubezpieczyciel odejmuje od każdego odszkodowania.', 'Kto pokrywa tę część szkody?', 'Ubezpieczony — z własnej kieszeni.'],
    steps: ['Odszkodowanie = szkoda − franszyza redukcyjna.', '8000 − 500 = 7500 zł.'],
    errors: [['8000', 'Pominięta franszyza.', 'Udział własny odejmuje się od odszkodowania.']],
  }),
  numeric({
    id: 'bf-u-4',
    skill: 'biz-insurance',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Dom wart 400 000 zł ubezpieczono na sumę 300 000 zł. Pożar spowodował szkodę 40 000 zł. Ile wypłaci ubezpieczyciel, stosując zasadę proporcji (w zł)?',
    answer: 30000,
    verify: () => (40000 * 300000) / 400000,
    hints: ['Czy dom był ubezpieczony na pełną wartość?', 'Nie — na 3/4 wartości.', 'Jak zasada proporcji zmienia odszkodowanie?', 'Wypłata stanowi taką część szkody, jaką suma ubezpieczenia stanowi w wartości domu.'],
    steps: ['Suma ubezpieczenia to 300 000 : 400 000 = 3/4 wartości.', 'Odszkodowanie: 40 000 · 3/4 = 30 000 zł.'],
    errors: [['40000', 'Pominięte niedoubezpieczenie.', 'Przy zaniżonej sumie odszkodowanie jest proporcjonalnie mniejsze.']],
  }),
  choice({
    id: 'bf-u-5',
    skill: 'biz-insurance',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Gdzie należy sprawdzić, w jakich sytuacjach ubezpieczyciel NIE wypłaci odszkodowania?',
    choices: ['w OWU — w części o wyłączeniach odpowiedzialności', 'w materiałach reklamowych ubezpieczyciela', 'w samej polisie, bo zawiera sumę ubezpieczenia', 'w ustawie o ubezpieczeniach obowiązkowych'],
    answer: 'A',
    hints: ['Który dokument opisuje szczegółowo zakres ubezpieczenia?', 'Ogólne warunki ubezpieczenia.', 'Czy reklama pokazuje ograniczenia?', 'Zwykle nie — wyłączenia są w OWU.'],
    steps: ['Zakres ochrony i wyłączenia opisują OWU.', 'Przed zakupem polisy trzeba przeczytać wyłączenia odpowiedzialności.'],
    errors: [
      ['B', 'Reklama nie opisuje ograniczeń.', 'Wyłączenia są w OWU.'],
      ['C', 'Polisa potwierdza zawarcie umowy i sumę, a wyłączenia opisują OWU.', 'Wyłączenia są w OWU.'],
      ['D', 'Ta ustawa dotyczy ubezpieczeń obowiązkowych, np. OC.', 'Zakres dobrowolnej polisy określają OWU.'],
    ],
  }),
  choice({
    id: 'bf-u-6',
    skill: 'biz-insurance',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Pan Nowak chciał obniżyć składkę za ubezpieczenie mieszkania i podał sumę ubezpieczenia równą połowie jego wartości. Jaki jest skutek przy częściowej szkodzie?',
    choices: ['odszkodowanie zostanie zmniejszone mniej więcej o połowę', 'odszkodowanie będzie pełne, bo szkoda jest częściowa', 'ubezpieczyciel odmówi wypłaty, bo suma jest za niska', 'składka zostanie zwrócona zamiast odszkodowania'],
    answer: 'A',
    hints: ['Jak nazywa się ubezpieczenie na sumę niższą od wartości mienia?', 'Niedoubezpieczenie.', 'Jaką zasadę stosuje wtedy ubezpieczyciel?', 'Zasadę proporcji.'],
    steps: ['Suma ubezpieczenia równa połowie wartości oznacza niedoubezpieczenie.', 'Wg zasady proporcji wypłata to ok. połowa szkody.'],
    errors: [
      ['B', 'Zasada proporcji działa także przy szkodach częściowych.', 'Odszkodowanie spada proporcjonalnie.'],
      ['C', 'Niedoubezpieczenie nie wyklucza wypłaty.', 'Odszkodowanie spada proporcjonalnie.'],
      ['D', 'Zwrot składki nie zastępuje odszkodowania.', 'Odszkodowanie spada proporcjonalnie.'],
    ],
  }),

  // biz-consumer-protection ---------------------------------------------------
  numeric({
    id: 'bf-k-1',
    skill: 'biz-consumer-protection',
    kind: 'foundation',
    difficulty: 1,
    prompt: 'Ile dni ma konsument na odstąpienie bez podania przyczyny od umowy zawartej przez internet?',
    answer: 14,
    hints: ['Jaki rodzaj umowy opisuje zadanie?', 'Zawartą na odległość.', 'Czy trzeba podawać powód?', 'Nie — prawo daje na to ustawowy termin w dniach.'],
    steps: ['Od umowy zawartej na odległość można odstąpić bez podania przyczyny.', 'Termin wynosi 14 dni.'],
    errors: [['30', 'Pomylono z terminem odpowiedzi banku na reklamację.', 'Odstąpienie od umowy na odległość: 14 dni.']],
  }),
  choice({
    id: 'bf-k-2',
    skill: 'biz-consumer-protection',
    kind: 'foundation',
    difficulty: 2,
    prompt: 'Bank odrzucił reklamację klienta dotyczącą niesłusznie pobranej opłaty. Która instytucja pomaga klientom w sporach z podmiotami rynku finansowego?',
    choices: ['Rzecznik Finansowy', 'Narodowy Bank Polski', 'Giełda Papierów Wartościowych', 'Główny Urząd Statystyczny'],
    answer: 'A',
    hints: ['Czyją rolą jest pomoc klientowi w konkretnym sporze z bankiem?', 'Rzecznika — obrońcy klientów.', 'Czy NBP rozstrzyga spory klientów z bankami?', 'Nie — zajmuje się polityką pieniężną.'],
    steps: ['Rzecznik Finansowy pomaga klientom po odrzuceniu reklamacji.', 'Może prowadzić postępowanie polubowne i interwencyjne.'],
    errors: [
      ['B', 'NBP prowadzi politykę pieniężną.', 'Spory klientów — Rzecznik Finansowy.'],
      ['C', 'GPW organizuje obrót papierami.', 'Spory klientów — Rzecznik Finansowy.'],
      ['D', 'GUS prowadzi statystykę.', 'Spory klientów — Rzecznik Finansowy.'],
    ],
  }),
  choice({
    id: 'bf-k-3',
    skill: 'biz-consumer-protection',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Marek kupił kurtkę w sklepie stacjonarnym. Po dwóch dniach stwierdził, że woli inny kolor (kurtka jest bez wad). Jaka jest jego sytuacja prawna?',
    choices: ['sklep nie musi przyjąć zwrotu — może zrobić to dobrowolnie', 'może złożyć reklamację z powodu niezgodności z umową', 'ma ustawowe 14 dni na odstąpienie od umowy', 'sklep musi oddać pieniądze w ciągu 24 godzin'],
    answer: 'A',
    hints: ['Czy towar ma wadę?', 'Nie — więc reklamacja nie przysługuje.', 'Czy umowę zawarto na odległość?', 'Nie — ustawowe prawo odstąpienia go nie obejmuje.'],
    steps: ['Brak wady — nie ma podstaw do reklamacji.', 'Zakup w sklepie stacjonarnym — zwrot tylko, jeśli sklep dobrowolnie na to pozwala.'],
    errors: [
      ['B', 'Reklamacja dotyczy wad towaru.', 'Kurtka jest bez wad.'],
      ['C', 'Prawo odstąpienia dotyczy umów na odległość i poza lokalem.', 'Zakup był w sklepie stacjonarnym.'],
      ['D', 'Nie ma takiego obowiązku.', 'Zwrot zależy od dobrej woli sklepu.'],
    ],
  }),
  numeric({
    id: 'bf-k-4',
    skill: 'biz-consumer-protection',
    kind: 'typical',
    difficulty: 3,
    prompt: 'Konsument złożył u sprzedawcy reklamację wadliwego towaru. W ciągu ilu dni sprzedawca musi odpowiedzieć, żeby reklamacja nie została uznana za przyjętą?',
    answer: 14,
    hints: ['Co się dzieje, gdy sprzedawca w ogóle nie odpowie?', 'Reklamację uznaje się za przyjętą.', 'Jak długi jest ten termin?', 'Tyle samo dni co na odstąpienie od umowy zawartej przez internet.'],
    steps: ['Sprzedawca ma 14 dni na odpowiedź na reklamację konsumenta.', 'Brak odpowiedzi w terminie oznacza uznanie reklamacji.'],
    errors: [['30', 'Pomylono z terminem dla banków.', 'Sprzedawca towaru ma 14 dni.']],
  }),
  choice({
    id: 'bf-k-5',
    skill: 'biz-consumer-protection',
    kind: 'transfer',
    difficulty: 4,
    prompt: 'Doradca w banku namawia 80-letnią klientkę, która chce bezpiecznie odłożyć oszczędności, do zakupu ryzykownego funduszu z wysoką prowizją, nie informując o ryzyku. Jak nazywa się taka praktyka?',
    choices: ['missellingu', 'dywersyfikacji portfela', 'kapitalizacji odsetek', 'doradztwa inwestycyjnego'],
    answer: 'A',
    hints: ['Czy produkt pasuje do celu klientki?', 'Nie — chce bezpieczeństwa, a dostaje ryzyko.', 'Czy doradca rzetelnie informuje?', 'Nie — ukrywa ryzyko, a zależy mu na prowizji.'],
    steps: ['Sprzedaż produktu niedopasowanego do potrzeb i wiedzy klienta to misselling.', 'To nieetyczna praktyka — można ją zgłosić do Rzecznika Finansowego, UOKiK lub KNF.'],
    errors: [
      ['B', 'Dywersyfikacja to rozkładanie ryzyka.', 'Tu produkt jest źle dopasowany.'],
      ['C', 'Kapitalizacja dotyczy odsetek.', 'Tu produkt jest źle dopasowany.'],
      ['D', 'Rzetelne doradztwo uwzględnia potrzeby i ryzyko klienta.', 'Ukrywanie ryzyka i złe dopasowanie to misselling.'],
    ],
  }),
  choice({
    id: 'bf-k-6',
    skill: 'biz-consumer-protection',
    kind: 'transfer',
    difficulty: 5,
    prompt: 'Fundusz inwestycyjny przy wyborze spółek bierze pod uwagę także wpływ ich działalności na środowisko, relacje z pracownikami i jakość zarządzania. Jak określa się takie podejście?',
    choices: ['zrównoważone finanse (kryteria ESG)', 'spekulacja krótkoterminowa', 'analiza techniczna', 'piramida finansowa'],
    answer: 'A',
    hints: ['Jakie trzy obszary wymieniono?', 'Środowisko, społeczeństwo (pracownicy), ład zarządczy.', 'Jak brzmi angielski skrót tych trzech obszarów?', 'E, S, G.'],
    steps: ['Środowisko, sprawy społeczne i ład korporacyjny to kryteria ESG.', 'Uwzględnianie ich to zrównoważone finanse.'],
    errors: [
      ['B', 'Spekulacja skupia się na krótkich zmianach cen.', 'Tu chodzi o kryteria ESG.'],
      ['C', 'Analiza techniczna bada wykresy notowań.', 'Tu chodzi o kryteria ESG.'],
      ['D', 'Piramida finansowa to oszustwo.', 'Tu chodzi o kryteria ESG.'],
    ],
  }),
];

// ===========================================================================
// Fiszki
// ===========================================================================

export const FIN_CARDS: Flashcard[] = [
  card('c-bf-m-1', 'biz-money-institutions', 'definicja', 'Cztery funkcje pieniądza?', 'Środek wymiany, miernik wartości, środek przechowywania wartości, środek płatniczy.'),
  card('c-bf-m-2', 'biz-money-institutions', 'definicja', 'NBP, KNF, BFG — kto co robi?', 'NBP: polityka pieniężna i emisja; KNF: nadzór; BFG: gwarancja depozytów.'),

  card('c-bf-h-1', 'biz-household', 'definicja', 'Poduszka finansowa?', 'Oszczędności na 3–6 miesięcy wydatków na nieprzewidziane sytuacje.'),
  card('c-bf-h-2', 'biz-household', 'pulapka', 'Czym grozi spłacanie długu nowym długiem?', 'Spiralą zadłużenia — dług i koszty rosną.'),

  card('c-bf-b-1', 'biz-banking', 'definicja', 'Co oznacza RRSO?', 'Rzeczywista roczna stopa oprocentowania wyraża całkowity koszt kredytu jako procent całkowitej kwoty kredytu w stosunku rocznym. Uwzględnia m.in. odsetki, prowizje i wymagane usługi dodatkowe; nie oznacza każdej dobrowolnej opłaty ani samej kwoty kosztu.'),
  card('c-bf-b-2', 'biz-banking', 'wzor', 'Odsetki netto: stała roczna stopa r, brak kapitalizacji w okresie i podatek 19%?', 'W uproszczeniu, bez zaokrągleń: kwota × r × miesiące/12 × 0,81. Stopę r zapisz jako ułamek; przy kapitalizacji w trakcie okresu potrzebny jest inny rachunek.'),

  card('c-bf-c-1', 'biz-compound-saving', 'wzor', 'Procent składany?', '$K_n = K_0 (1 + r)^n$'),
  card('c-bf-c-2', 'biz-compound-saving', 'metoda', 'Reguła 72?', 'Lata do podwojenia ≈ 72 : oprocentowanie (%).'),

  card('c-bf-i-1', 'biz-investing', 'definicja', 'Akcja a obligacja?', 'Akcja: udział we własności. Obligacja: dług emitenta.'),
  card('c-bf-i-2', 'biz-investing', 'wzor', 'Stopa zwrotu z akcji?', '(cena sprzedaży + dywidendy − cena zakupu) : cena zakupu.'),

  card('c-bf-u-1', 'biz-insurance', 'wzor', 'Zasada proporcji?', 'odszkodowanie = szkoda · suma ubezpieczenia : wartość mienia.'),
  card('c-bf-u-2', 'biz-insurance', 'definicja', 'OC a AC?', 'OC (obowiązkowe): szkody wyrządzone innym. AC (dobrowolne): szkody we własnym aucie.'),

  card('c-bf-k-1', 'biz-consumer-protection', 'definicja', 'Odstąpienie konsumenta od umowy na odległość?', 'Co do zasady 14 dni bez podania przyczyny. Istnieją ustawowe wyjątki, np. towar wykonany według indywidualnej specyfikacji konsumenta; nie jest to prawo zwrotu każdej rzeczy.'),
  card('c-bf-k-2', 'biz-consumer-protection', 'definicja', 'Gdzie po pomoc w sporze z bankiem?', 'Rzecznik Finansowy (po odrzuceniu reklamacji).'),
];
